import { useEffect, useState, useCallback } from 'react';
import { servicesAPI, adminAPI, virtualNumbersAPI } from '../api';
import { Settings, Edit2 } from 'react-feather';
import './AdminServices.css';

const SUPPLIER_ORDER = ['exo', 'smm_africa'];

function getSupplierLabel(supplierCode) {
  if (supplierCode === 'exo') return 'EXO';
  if (supplierCode === 'smm_africa') return 'SMM Africa';
  return supplierCode || 'Unknown Supplier';
}

// ── VN Service Row ─────────────────────────────────────────────────────────────
function VnServiceRow({ svc, vnMultiplier }) {
  const [status, setStatus] = useState(svc.status || 'active');
  const [customPrice, setCustomPrice] = useState(
    svc.custom_price != null ? parseFloat(svc.custom_price).toFixed(4) : ''
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await virtualNumbersAPI.updateOverride({
        service_code: svc.code,
        country_id: 0,
        custom_price: customPrice === '' ? null : parseFloat(customPrice),
        status,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vn-service-row">
      <div className="vn-service-row-name">
        <span className="vn-service-code">{svc.code}</span>
        <span className="vn-service-name">{svc.name}</span>
      </div>
      <div className="vn-service-row-controls">
        <select
          className="form-select vn-inline-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        <input
          className="form-input vn-inline-price"
          type="number"
          step="0.0001"
          min="0"
          placeholder={`Auto (×${vnMultiplier})`}
          value={customPrice}
          onChange={(e) => setCustomPrice(e.target.value)}
        />
        <button
          className={`btn btn-sm ${saved ? 'btn-secondary' : 'btn-primary'} vn-save-btn`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <span className="spinner-xs" /> : saved ? 'Saved' : 'Save'}
        </button>
      </div>
      {error && <div className="vn-row-error">{error}</div>}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [editSvc, setEditSvc] = useState(null);
  const [editForm, setEditForm] = useState({ custom_price: '', status: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [multiplier, setMultiplier] = useState('1.3');
  const [savingMul, setSavingMul] = useState(false);
  const [mulMsg, setMulMsg] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Virtual Numbers state
  const [activeTab, setActiveTab] = useState('smm');
  const [vnMultiplier, setVnMultiplier] = useState('1.3');
  const [vnStatus, setVnStatus] = useState('beta');
  const [vnProvider, setVnProvider] = useState('smspool');
  const [savingVn, setSavingVn] = useState(false);
  const [vnMsg, setVnMsg] = useState('');
  const [vnServices, setVnServices] = useState([]);
  const [vnSearch, setVnSearch] = useState('');
  const [vnFilter, setVnFilter] = useState('all');

  const loadAll = (retries = 3) => {
    setLoading(true);
    const attemptLoad = (attempt = 0) => {
      Promise.all([
        servicesAPI.adminAll(),
        adminAPI.settings(),
        virtualNumbersAPI.getServices().catch(() => ({ data: { services: [] } })),
      ])
        .then(([svcRes, settingsRes, vnRes]) => {
          setServices(Array.isArray(svcRes.data?.services) ? svcRes.data.services : []);
          setMultiplier(settingsRes.data?.settings?.profit_multiplier || '1.3');
          setVnMultiplier(settingsRes.data?.settings?.virtual_numbers_multiplier || '1.3');
          setVnStatus(settingsRes.data?.settings?.virtual_numbers_status || 'beta');
          setVnProvider(settingsRes.data?.settings?.virtual_numbers_provider || 'smspool');
          setVnServices(Array.isArray(vnRes.data?.services) ? vnRes.data.services : []);
          setLoading(false);
        })
        .catch(err => {
          if (attempt < retries && (err.response?.status >= 500 || !err.response)) {
            setTimeout(() => attemptLoad(attempt + 1), 1000 * (attempt + 1));
          } else {
            setLoading(false);
            console.error('Failed to load services:', err);
          }
        });
    };
    attemptLoad();
  };

  useEffect(() => { loadAll(); }, []);

  const openEdit = (service) => {
    setEditSvc(service);
    setEditForm({
      custom_price: service.custom_price != null ? parseFloat(service.custom_price).toFixed(4) : '',
      status: service.status
    });
    setMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await servicesAPI.adminUpdate(editSvc.id, {
        custom_price: editForm.custom_price === '' ? null : parseFloat(editForm.custom_price),
        status: editForm.status
      });
      setServices(prev => prev.map(service =>
        service.id === editSvc.id
          ? { ...service, custom_price: editForm.custom_price === '' ? null : editForm.custom_price, status: editForm.status }
          : service
      ));
      setMsg('Saved');
      setTimeout(() => setEditSvc(null), 1000);
    } catch (error) {
      setMsg(error.response?.data?.error || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const saveMul = async () => {
    setSavingMul(true);
    setMulMsg('');
    try {
      await adminAPI.updateSettings({ profit_multiplier: multiplier });
      setMulMsg('Multiplier saved. Re-sync services to apply.');
    } catch {
      setMulMsg('Failed to save');
    } finally {
      setSavingMul(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMulMsg('');
    try {
      const res = await servicesAPI.adminSync();
      setMulMsg(`Sync completed. ${res.data?.total || 0} services currently in the catalog.`);
      loadAll();
    } catch (error) {
      setMulMsg(error.response?.data?.error || 'Service sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const saveVnSettings = async () => {
    setSavingVn(true);
    setVnMsg('');
    try {
      await adminAPI.updateSettings({
        virtual_numbers_multiplier: vnMultiplier,
        virtual_numbers_status: vnStatus,
        virtual_numbers_provider: vnProvider
      });
      setVnMsg('Virtual Number settings saved successfully.');
    } catch {
      setVnMsg('Failed to save settings');
    } finally {
      setSavingVn(false);
    }
  };

  // SMM filtered + grouped
  const filtered = services.filter((service) => {
    const query = search.toLowerCase();
    const matchSearch =
      !search ||
      service.name?.toLowerCase().includes(query) ||
      service.category?.toLowerCase().includes(query);
    const matchStatus = !filterStatus || service.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const groupedBySupplier = SUPPLIER_ORDER
    .map((supplierCode) => ({
      supplierCode,
      supplierLabel: getSupplierLabel(supplierCode),
      services: filtered.filter((service) => (service.supplier_code || 'exo') === supplierCode),
    }))
    .filter((group) => group.services.length > 0)
    .concat(
      Object.entries(
        filtered.reduce((acc, service) => {
          const supplierCode = service.supplier_code || 'exo';
          if (SUPPLIER_ORDER.includes(supplierCode)) return acc;
          if (!acc[supplierCode]) acc[supplierCode] = [];
          acc[supplierCode].push(service);
          return acc;
        }, {})
      ).map(([supplierCode, supplierServices]) => ({
        supplierCode,
        supplierLabel: getSupplierLabel(supplierCode),
        services: supplierServices,
      }))
    );

  const filteredVnServices = vnServices.filter((svc) => {
    const query = vnSearch.toLowerCase();
    const matchSearch =
      !vnSearch ||
      svc.name?.toLowerCase().includes(query) ||
      svc.code?.toLowerCase().includes(query);
    const matchFilter =
      vnFilter === 'all' ||
      (vnFilter === 'active' && svc.status !== 'disabled') ||
      (vnFilter === 'disabled' && svc.status === 'disabled');
    return matchSearch && matchFilter;
  });

  return (
    <div className="admin-services-page">
      <h1 className="page-title">Services</h1>
      <p className="page-subtitle">
        {activeTab === 'smm'
          ? `${services.length} total SMM services across ${new Set(services.map(s => s.supplier_code || 'exo')).size} suppliers`
          : 'Configure virtual phone number settings'
        }
      </p>

      {/* Tabs */}
      <div className="services-tabs" style={{ display: 'flex', gap: 12, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        <button
          className={`tab-btn ${activeTab === 'smm' ? 'active' : ''}`}
          style={{
            padding: '12px 18px', background: 'none', border: 'none',
            borderBottom: activeTab === 'smm' ? '3px solid var(--accent)' : '3px solid transparent',
            color: activeTab === 'smm' ? 'var(--text)' : 'var(--text-muted)',
            fontWeight: 700, cursor: 'pointer', fontSize: 15
          }}
          onClick={() => setActiveTab('smm')}
        >
          SMM Services
        </button>
        <button
          className={`tab-btn ${activeTab === 'virtual_numbers' ? 'active' : ''}`}
          style={{
            padding: '12px 18px', background: 'none', border: 'none',
            borderBottom: activeTab === 'virtual_numbers' ? '3px solid var(--accent)' : '3px solid transparent',
            color: activeTab === 'virtual_numbers' ? 'var(--text)' : 'var(--text-muted)',
            fontWeight: 700, cursor: 'pointer', fontSize: 15
          }}
          onClick={() => setActiveTab('virtual_numbers')}
        >
          Virtual Phone Numbers
        </button>
      </div>

      {activeTab === 'smm' ? (
        <>
          {/* SMM Multiplier Card */}
          <div className="card admin-services-card" style={{ marginBottom: 24, maxWidth: 480 }}>
            <h3 style={{ marginBottom: 4, fontSize: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Settings size={18} /> Global Profit Multiplier
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
              Final price = supplier price × multiplier. Applied to all services without a custom price.
            </p>
            {mulMsg && (
              <div className={`alert ${mulMsg.startsWith('Multiplier saved') ? 'alert-success' : 'alert-error'}`}>
                {mulMsg}
              </div>
            )}
            <div className="admin-services-actions" style={{ display: 'flex', gap: 10 }}>
              <input
                className="form-input"
                type="number"
                step="0.01"
                min="1"
                style={{ maxWidth: 140 }}
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
              />
              <button className="btn btn-primary" onClick={saveMul} disabled={savingMul}>
                {savingMul ? 'Saving...' : 'Save Multiplier'}
              </button>
              <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
                {syncing ? 'Syncing...' : 'Sync Services'}
              </button>
            </div>
          </div>

          {/* SMM Services Table */}
          <div className="card admin-services-card">
            <div className="admin-services-filters" style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <input
                className="form-input"
                style={{ maxWidth: 280 }}
                placeholder="Search by name or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="form-select"
                style={{ maxWidth: 160 }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
              <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
                Showing {filtered.length} services
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div className="spinner" style={{ margin: '0 auto' }} />
              </div>
            ) : (
              <div className="admin-services-sections">
                {groupedBySupplier.map(({ supplierCode, supplierLabel, services: supplierServices }) => (
                  <section key={supplierCode} className="admin-services-supplier-section">
                    <div className="admin-services-supplier-header">
                      <div>
                        <h3 className="admin-services-supplier-title">{supplierLabel}</h3>
                        <p className="admin-services-supplier-subtitle">
                          {supplierServices.length} service{supplierServices.length === 1 ? '' : 's'}
                        </p>
                      </div>
                    </div>
                    <div className="table-wrap admin-services-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>{supplierLabel} ID</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Supplier Price</th>
                            <th>Calc Price</th>
                            <th>Custom Price</th>
                            <th>Final</th>
                            <th>Min</th>
                            <th>Max</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supplierServices.map((service) => {
                            const supplierPrice = parseFloat(service.supplier_price || 0);
                            const calcPrice = (supplierPrice * parseFloat(multiplier || 1)).toFixed(4);
                            const finalPrice = service.custom_price != null ? parseFloat(service.custom_price).toFixed(4) : calcPrice;

                            return (
                              <tr key={service.id}>
                                <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{service.id}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{service.supplier_service_id}</td>
                                <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }} title={service.name}>{service.name}</td>
                                <td style={{ fontSize: 12 }}>{service.category}</td>
                                <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>${supplierPrice.toFixed(4)}</td>
                                <td style={{ fontSize: 13 }}>${calcPrice}</td>
                                <td>
                                  {service.custom_price != null ? (
                                    <span style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 13 }}>
                                      ${parseFloat(service.custom_price).toFixed(4)}
                                    </span>
                                  ) : (
                                    <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>-</span>
                                  )}
                                </td>
                                <td style={{ color: 'var(--green-500)', fontWeight: 700, fontSize: 13 }}>${finalPrice}</td>
                                <td style={{ fontSize: 12 }}>{service.min?.toLocaleString?.() ?? service.min}</td>
                                <td style={{ fontSize: 12 }}>{service.max?.toLocaleString?.() ?? service.max}</td>
                                <td><span className={`badge badge-${service.status}`}>{service.status}</span></td>
                                <td>
                                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => openEdit(service)}>
                                    <Edit2 size={14} style={{ marginRight: 4 }} /> Edit
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="card admin-services-card" style={{ maxWidth: 720 }}>
          <h3 style={{ marginBottom: 4, fontSize: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Settings size={18} /> Virtual Numbers Configuration
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>
            Configure active server provider, global markup rate, and overall service status for Virtual Phone Numbers.
          </p>
          {vnMsg && (
            <div className={`alert ${vnMsg.includes('saved') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 14 }}>
              {vnMsg}
            </div>
          )}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Active Server / Provider
              </label>
              <select
                className="form-select"
                style={{ minWidth: 260 }}
                value={vnProvider}
                onChange={(e) => setVnProvider(e.target.value)}
              >
                <option value="both">Both Enabled (User Selects Provider)</option>
                <option value="smspool">Provider 1 Only (Default)</option>
                <option value="5sim">Provider 2 Only</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 6 }}>Global Multiplier</label>
              <input
                className="form-input"
                type="number"
                step="0.01"
                min="0.1"
                style={{ maxWidth: 120 }}
                value={vnMultiplier}
                onChange={(e) => setVnMultiplier(e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 6 }}>Service Status</label>
              <select
                className="form-select"
                style={{ maxWidth: 180 }}
                value={vnStatus}
                onChange={(e) => setVnStatus(e.target.value)}
              >
                <option value="active">Active (Everyone)</option>
                <option value="beta">Beta (Testers Only)</option>
                <option value="disabled">Disabled (Hidden)</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={saveVnSettings} disabled={savingVn}>
              {savingVn ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {/* ── Virtual Number Services Table ── */}
          <div className="card admin-services-card" style={{ marginTop: 24 }}>
            <div className="admin-services-filters" style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <input
                className="form-input"
                style={{ maxWidth: 280 }}
                placeholder="Search by name or code..."
                value={vnSearch}
                onChange={(e) => setVnSearch(e.target.value)}
              />
              <select
                className="form-select"
                style={{ maxWidth: 160 }}
                value={vnFilter}
                onChange={(e) => setVnFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="disabled">Inactive</option>
              </select>
              <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
                Showing {filteredVnServices.length} of {vnServices.length} services
              </div>
            </div>

            <div className="vn-services-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredVnServices.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
                  No virtual number services found.
                </div>
              ) : (
                filteredVnServices.map((svc) => (
                  <VnServiceRow key={svc.code} svc={svc} vnMultiplier={vnMultiplier} />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* SMM Edit Modal */}
      {editSvc && (
        <div className="modal-overlay" onClick={() => setEditSvc(null)}>
          <div className="modal admin-services-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Edit Service</h3>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {editSvc.name}
            </div>
            {msg && (
              <div className={`alert ${msg.startsWith('Saved') ? 'alert-success' : 'alert-error'}`}>
                {msg}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Custom Price per 1000 (USD)</label>
              <input
                className="form-input"
                type="number"
                step="0.0001"
                min="0"
                placeholder={`Leave empty to use calculated`}
                value={editForm.custom_price}
                onChange={(e) => setEditForm({ ...editForm, custom_price: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditSvc(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
